import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import RideAddress from './RideAddress';
import {
  fetchRide,
  postAddressStatus,
  buildAddressStatusPayload,
  applyAddressResult,
  sortAddresses,
  loadPendingRide,
  savePendingRide,
  flushOtherRideCaches,
  addConfirmedAddress,
  addConfirmedAddressFromPayload,
  mergeConfirmedRide,
  rideUrl,
  rideEventUrl
} from '../rideApi';
import { logRideEvent } from '../rideTimeline';
import { rideDebugData } from '../debug/rideDebugData';
import './RidePage.css';

// Retry an unsynced address every 15s while offline/unreachable, on top of
// retrying immediately whenever the browser reports it's back online.
const RETRY_INTERVAL_MS = 15000;

// Courier flow: /:conneoId/rit/:rideId  (debug: /DEBUG/rit/:jsonName)
//
// The ride lives in React state; addresses are handled optimistically so a
// courier can keep delivering with no/flaky signal (e.g. buitengebied): a
// submit is tried immediately, and only queued locally for background retry
// if it fails. While that queue is non-empty, the ride (with its local
// changes) is cached so a refresh doesn't refetch a stale copy from the
// server and lose them; once everything has synced, refreshing goes back to
// fetching the current server state as normal.
const RidePage = ({ debug = false }) => {
  const { conneoId, rideId, jsonName } = useParams();
  const [ride, setRide] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [debugPayload, setDebugPayload] = useState(null);
  const [log, setLog] = useState([]);
  const [pendingQueue, setPendingQueue] = useState([]);
  const [submitError, setSubmitError] = useState(null);

  const pendingKeys = useMemo(() => new Set(pendingQueue.map(item => item.addressKey)), [pendingQueue]);

  // Small on-device log of address submissions: one row per address (updated in
  // place as its status changes, most recently updated on top), not a running
  // history of every attempt. Shown on the info screen so a courier can be
  // asked to open it and screenshot it when something goes wrong, without
  // needing access to the backend/server logs.
  // status: 'success' | 'pending' (queued, will retry) | 'error' (won't retry).
  const logAddress = (addressKey, address, status) => {
    setLog(prev => {
      const time = new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
      const rest = prev.filter(entry => entry.addressKey !== addressKey);
      return [{ addressKey, address, status, time }, ...rest];
    });
  };

  const rideLabel = debug ? jsonName : rideId;

  useEffect(() => {
    document.title = 'Conneo Scan > koerier';
  }, []);

  // Load the ride once, when the page is opened or refreshed. If there's still
  // an unsynced queue for this ride, load that cached (locally modified) copy
  // instead of refetching from the server, so a refresh can't lose it.
  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setSubmitError(null);
    setRide(null);
    setSelectedKey(null);
    setPendingQueue([]);

    const load = async () => {
      try {
        let data;
        let queue = [];
        if (debug) {
          const json = rideDebugData[jsonName];
          if (!json) {
            throw new Error(`Debug JSON '${jsonName}' not found. Available: ${Object.keys(rideDebugData).join(', ')}`);
          }
          data = json.data;
        } else {
          // Fire-and-forget: tries to sync any other ride's still-unsynced
          // items in the background. Never awaited - it must not delay
          // opening today's actual ride.
          flushOtherRideCaches(conneoId, rideId);
          const cached = loadPendingRide(conneoId, rideId);
          if (cached && cached.pendingQueue && cached.pendingQueue.length > 0) {
            data = cached.ride;
            queue = cached.pendingQueue;
          } else {
            data = await fetchRide(conneoId, rideId, controller.signal);
            // A fresh fetch may not yet reflect a delivery result the server
            // only just accepted (it's processed asynchronously) - reapply any
            // such still-unconfirmed local results so a hard refresh can't
            // briefly "undo" an already-successful delivery.
            data = mergeConfirmedRide(conneoId, rideId, data);
          }
        }
        if (!ignore) {
          setRide(data);
          setPendingQueue(queue);
        }
        if (!ignore && !debug) {
          const addressCount = data && Array.isArray(data.addresses) ? data.addresses.length : 0;
          logRideEvent(conneoId, rideId, {
            eventName: queue.length > 0 ? 'RideOpenedFromPendingCache' : 'RideOpened',
            eventMessage: queue.length > 0
              ? `Rit geopend vanuit lokale cache (${queue.length} nog niet gesynchroniseerd)`
              : `Rit geopend (${addressCount} adressen)`,
            functionName: 'RidePage.load',
            apiRequestUrl: rideUrl(conneoId, rideId),
            apiRequestResponseData: data,
            extraData: { ride: data, pendingQueue: queue }
          });
        }
      } catch (err) {
        if (!ignore && err.name !== 'AbortError') {
          setError(err.message);
          if (!debug) {
            logRideEvent(conneoId, rideId, {
              eventName: 'RideLoadFailed',
              eventType: 'Error',
              eventMessage: err.message,
              functionName: 'RidePage.load',
              apiRequestUrl: rideUrl(conneoId, rideId),
              extraData: { error: err.message }
            });
          }
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [debug, conneoId, rideId, jsonName]);

  // Persist the ride + queue while there's still something unsynced (see load
  // effect above); drop the cache again as soon as everything has synced.
  useEffect(() => {
    if (debug || !ride) return;
    savePendingRide(conneoId, rideId, ride, pendingQueue);
  }, [debug, conneoId, rideId, ride, pendingQueue]);

  // Retry unsynced addresses: immediately when the queue changes, again
  // whenever the browser reports it's back online, and periodically in
  // between to also catch a flaky connection that never truly goes "offline".
  useEffect(() => {
    if (debug || pendingQueue.length === 0) return;
    let cancelled = false;

    const flush = async () => {
      for (const item of pendingQueue) {
        if (cancelled) return;
        try {
          await postAddressStatus(conneoId, rideId, item.payload);
          if (cancelled) return;
          logAddress(item.addressKey, item.addressLabel, 'success');
          setPendingQueue(prev => prev.filter(p => p !== item));
          addConfirmedAddressFromPayload(conneoId, rideId, item.addressKey, item.payload);
          logRideEvent(conneoId, rideId, {
            eventName: 'AddressSyncedAfterRetry',
            eventMessage: `Adres alsnog gesynchroniseerd na eerdere fout: ${item.addressLabel}`,
            functionName: 'RidePage.flush',
            apiRequestUrl: rideEventUrl(conneoId, rideId),
            apiRequestContent: item.payload,
            extraData: { addressKey: item.addressKey, addressLabel: item.addressLabel, payload: item.payload }
          });
        } catch (err) {
          if (cancelled) return;
          if (!err.retryable) {
            // Permanent failure (e.g. the server rejects this payload): retrying
            // it every 15s forever can never succeed, so stop and surface it.
            logAddress(item.addressKey, item.addressLabel, 'error');
            setPendingQueue(prev => prev.filter(p => p !== item));
            setSubmitError(`${item.addressLabel}: ${err.message}. Neem contact op met Andi.`);
            logRideEvent(conneoId, rideId, {
              eventName: 'AddressSyncFailedPermanently',
              eventType: 'Error',
              eventMessage: err.message,
              functionName: 'RidePage.flush',
              apiRequestUrl: rideEventUrl(conneoId, rideId),
              apiRequestContent: item.payload,
              extraData: { addressKey: item.addressKey, addressLabel: item.addressLabel, payload: item.payload, error: err.message }
            });
          }
          // else: still unreachable/failing; leave it queued and try again later
        }
      }
    };

    flush();
    const interval = setInterval(flush, RETRY_INTERVAL_MS);
    window.addEventListener('online', flush);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('online', flush);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debug, conneoId, rideId, pendingQueue]);

  const sortedAddresses = useMemo(() => sortAddresses(ride ? ride.addresses : []), [ride]);

  const selectedAddress = useMemo(
    () => (ride && selectedKey ? ride.addresses.find(a => a.key === selectedKey) : null),
    [ride, selectedKey]
  );

  // Called by RideAddress. Builds the new ride and the address-status payload,
  // then commits it to state right away (optimistic) so the courier can keep
  // delivering regardless of connectivity. The submit is tried immediately;
  // if it fails it's queued for background retry instead of blocking here.
  const handleProcessAddress = async (addressKey, result) => {
    let payload;
    let address;

    try {
      const nextRide = applyAddressResult(ride, addressKey, result);
      payload = buildAddressStatusPayload(result);
      address = ride.addresses.find(a => a.key === addressKey);

      setRide(nextRide);
      setSelectedKey(null);

      if (debug) {
        console.log('[DEBUG MODE] Payload that would be sent:', JSON.stringify(payload, null, 2));
        setDebugPayload(payload);
        return;
      }
    } catch (err) {
      // Unexpected failure building the update itself (not a network/server
      // problem) - RideAddress's own try/catch around `await onProcess(...)`
      // will show err.message to the courier; this just makes sure it's also
      // traced, since it's rethrown as a rejected promise either way.
      logRideEvent(conneoId, rideId, {
        eventName: 'AddressProcessingCrashed',
        eventType: 'Error',
        eventMessage: err.message,
        functionName: 'RidePage.handleProcessAddress',
        extraData: { addressKey, error: err.message, stack: err.stack }
      });
      throw err;
    }

    const addressLabel = address ? address.afleverNaam : addressKey;

    try {
      await postAddressStatus(conneoId, rideId, payload);
      logAddress(addressKey, addressLabel, 'success');
      addConfirmedAddress(conneoId, rideId, addressKey, result);
      logRideEvent(conneoId, rideId, {
        eventName: 'AddressProcessed',
        eventMessage: `Adres afgemeld: ${addressLabel} (${payload.status})`,
        functionName: 'RidePage.handleProcessAddress',
        apiRequestUrl: rideEventUrl(conneoId, rideId),
        apiRequestContent: payload,
        extraData: { addressKey, addressLabel, address, payload }
      });
    } catch (err) {
      if (err.retryable) {
        logAddress(addressKey, addressLabel, 'pending');
        setPendingQueue(prev => [...prev, { addressKey, addressLabel, payload }]);
        logRideEvent(conneoId, rideId, {
          eventName: 'AddressSubmitQueued',
          eventType: 'Warning',
          eventMessage: err.message,
          functionName: 'RidePage.handleProcessAddress',
          apiRequestUrl: rideEventUrl(conneoId, rideId),
          apiRequestContent: payload,
          extraData: { addressKey, addressLabel, address, payload, error: err.message }
        });
      } else {
        // Permanent failure (e.g. the server rejects this payload): retrying it
        // forever can never succeed, so stop and surface it instead.
        logAddress(addressKey, addressLabel, 'error');
        setSubmitError(`${addressLabel}: ${err.message}. Neem contact op met Andi.`);
        logRideEvent(conneoId, rideId, {
          eventName: 'AddressSubmitFailed',
          eventType: 'Error',
          eventMessage: err.message,
          functionName: 'RidePage.handleProcessAddress',
          apiRequestUrl: rideEventUrl(conneoId, rideId),
          apiRequestContent: payload,
          extraData: { addressKey, addressLabel, address, payload, error: err.message }
        });
      }
    }
  };

  const handleOpenInfo = () => {
    logRideEvent(conneoId, rideId, {
      eventName: 'HelpOpened',
      eventMessage: 'Hulppagina geopend',
      functionName: 'RidePage.handleOpenInfo'
    });
    setShowInfo(true);
  };

  const handleCloseInfo = () => {
    logRideEvent(conneoId, rideId, {
      eventName: 'HelpClosed',
      eventMessage: 'Hulppagina gesloten',
      functionName: 'RidePage.handleCloseInfo'
    });
    setShowInfo(false);
  };

  const handleSelectAddress = (address) => {
    logRideEvent(conneoId, rideId, {
      eventName: 'AddressOpened',
      eventMessage: `Adres geopend: ${address.afleverNaam}`,
      functionName: 'RidePage.handleSelectAddress',
      extraData: { addressKey: address.key, addressLabel: address.afleverNaam, address }
    });
    setSelectedKey(address.key);
  };

  // Only fires for the explicit "back" button (abandoning without submitting);
  // a successful/failed submit closes the screen via handleProcessAddress
  // itself and is already logged there under its own event name.
  const handleCloseAddress = () => {
    if (selectedAddress) {
      logRideEvent(conneoId, rideId, {
        eventName: 'AddressClosed',
        eventMessage: `Adres gesloten zonder te versturen: ${selectedAddress.afleverNaam}`,
        functionName: 'RidePage.handleCloseAddress',
        extraData: { addressKey: selectedAddress.key, addressLabel: selectedAddress.afleverNaam }
      });
    }
    setSelectedKey(null);
  };

  const handleViewRemark = (address) => {
    logRideEvent(conneoId, rideId, {
      eventName: 'RemarkViewed',
      eventMessage: `Opmerking bekeken: ${address.afleverNaam}`,
      functionName: 'RidePage.handleViewRemark',
      extraData: { addressKey: address.key, addressLabel: address.afleverNaam, opmerking: address.opmerking }
    });
    alert(address.opmerking);
  };

  const carrierName = (ride && ride.carrier && ride.carrier.name) || 'Koerier';
  const deliveryDate = ride && ride.ride ? ride.ride.deliveryDate : '';
  const rideNumber = ride && ride.ride ? ride.ride.rideNumber : rideLabel;

  return (
    <div className="ride-page">
      <header className="ride-header">
        <span className="ride-header-title">{carrierName}</span>
        <button
          type="button"
          className="ride-header-info"
          onClick={handleOpenInfo}
          aria-label="Info"
        >
          ?
        </button>
      </header>

      <div className="ride-meta">
        <span>Leverdatum: {deliveryDate}</span>
        <span>Ritnr. {rideNumber}</span>
      </div>

      {debug && (
        <div className="ride-debug-banner">🐛 DEBUG MODE: {jsonName}</div>
      )}

      {error && (
        <div className="ride-error">
          <p className="error-message">{error}</p>
        </div>
      )}

      {submitError && !error && (
        <div className="ride-error ride-submit-error">
          <p className="error-message">⚠ {submitError}</p>
          <button type="button" className="ride-link" onClick={() => setSubmitError(null)}>
            Sluiten
          </button>
        </div>
      )}

      {loading && !error && <p className="ride-loading">Rit wordt geladen...</p>}

      {ride && !selectedAddress && !showInfo && (
        <div className="ride-list">
          {sortedAddresses.length === 0 && (
            <p className="ride-empty">Geen adressen in deze rit.</p>
          )}
          {sortedAddresses.map(address => (
            <RideListItem
              key={address.key}
              address={address}
              pending={pendingKeys.has(address.key)}
              onSelect={() => handleSelectAddress(address)}
              onViewRemark={() => handleViewRemark(address)}
            />
          ))}
        </div>
      )}

      {ride && selectedAddress && !showInfo && (
        <RideAddress
          address={selectedAddress}
          conneoId={conneoId}
          rideId={rideId}
          onBack={handleCloseAddress}
          onProcess={handleProcessAddress}
        />
      )}

      {showInfo && (
        <RideInfo log={log} onBack={handleCloseInfo} />
      )}

      {debugPayload && (
        <div className="ride-modal-backdrop" onClick={() => setDebugPayload(null)}>
          <div className="ride-modal ride-modal-wide" onClick={e => e.stopPropagation()}>
            <h3 className="ride-modal-title">🐛 DEBUG MODE - Payload JSON</h3>
            <pre className="ride-debug-json">{JSON.stringify(debugPayload, null, 2)}</pre>
            <button type="button" className="ride-btn ride-btn-primary" onClick={() => setDebugPayload(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const RideListItem = ({ address, pending, onSelect, onViewRemark }) => {
  const hasRemark = !!(address.opmerking && address.opmerking.trim() !== '');
  const hasDeliveryTime = !!(address.levertijd && address.levertijd.trim() !== '');
  const buttonColor = address.color || '#375FCF';

  return (
    <div className="ride-item">
      <div className="ride-item-address">
        {address.afleverNaam}<br />
        {address.afleverAdres} {address.afleverHuisNr}<br />
        {address.afleverPostcode} {address.afleverPlaats}
      </div>
      {hasRemark && (
        <button
          type="button"
          className="ride-item-remark"
          onClick={onViewRemark}
          aria-label="Opmerking"
          title={address.opmerking}
        >
          ⚠
        </button>
      )}
      <div className="ride-item-footer">
        <div className="ride-item-summary">
          <strong>{address.pallets} pallets, {address.verpakking} colli</strong>
          {hasDeliveryTime && (
            <div className="ride-item-time">Bezorgen voor {address.levertijd}</div>
          )}
        </div>
        <button
          type="button"
          className="ride-btn ride-item-button"
          style={{ backgroundColor: buttonColor }}
          onClick={onSelect}
        >
          {address.processed ? 'Verwerkt' : 'Afleveren'}
        </button>
      </div>
      {pending && <div className="ride-item-pending">⏳ Wacht op verbinding…</div>}
    </div>
  );
};

// Info screen: Andi's contact details plus a session-local log of address
// submissions, so a courier can be asked to open this and screenshot it when
// something goes wrong (no access to backend logs needed).
const RideInfo = ({ log, onBack }) => (
  <div className="ride-detail">
    <p className="ride-info-text">
      Andi Smart Print Solutions<br />
      Afrikalaan 40<br />
      6199 AH Maastricht-Airport<br />
      <br />
      tel: <a href="tel:+31433667160">+31(0)433667160</a>
    </p>

    <h3 className="ride-log-title">Verzendlog</h3>
    {log.length === 0 && <p className="ride-log-empty">Nog geen berichten verzonden.</p>}
    {log.length > 0 && (
      <ul className="ride-log-list">
        {log.map((entry, i) => (
          <li key={i} className="ride-log-item">
            <span className="ride-log-time">{entry.time}</span>
            <span className="ride-log-address">{entry.address}</span>
            <span className={`ride-log-status ride-log-status-${entry.status}`}>
              {entry.status === 'success' && 'afgeleverd op server'}
              {entry.status === 'pending' && 'in wachtrij (geen verbinding)'}
              {entry.status === 'error' && 'fout bij afleveren'}
            </span>
          </li>
        ))}
      </ul>
    )}

    <div className="ride-actions">
      <button type="button" className="ride-btn ride-btn-back" onClick={onBack}>
        &lt;&lt;
      </button>
    </div>
  </div>
);

export default RidePage;
